import type { Color, Move, PieceKind, Square } from '../game/types.js';
import type { Variant } from '../settings/types.js';
import { squareToAlgebraic } from './notation.js';

/**
 * Tag for cursor-narration context. Set by callers (main.ts) based on the
 * current TurnState so the announcer can hint at "valid move" / "capture
 * target" without depending on the TurnState class directly.
 */
export type CursorHint = 'none' | 'validMove' | 'validCapture';

export interface CursorOccupant {
  readonly color: Color;
  readonly kind: PieceKind;
}

/** Mode label for "you" vs "Player" framing. Mirrors backgammon. */
export type AnnouncerMode = 'ai' | 'online' | 'local';

/**
 * Translation function — same shape as `t` from `@bn-games/i18n-core`, but
 * injectable so the announcer is unit-testable without booting i18next.
 */
export type Translator = (key: string, vars?: Record<string, string | number>) => string;

export interface AnnouncerContextProvider {
  readonly getMode: () => AnnouncerMode;
  readonly getLocalColor: () => Color | null;
  readonly getLocalName: () => string | undefined;
  readonly getOpponentName: () => string | undefined;
  readonly getVariant: () => Variant;
}

export interface NarratedEvent {
  readonly text: string;
  /** When true, route through the assertive live region (interrupts SR). */
  readonly assertive?: boolean;
}

export type GameOutcome =
  | { readonly kind: 'win'; readonly winner: Color }
  | { readonly kind: 'draw' };

/**
 * Stateless formatter that turns a single committed move (or turn change,
 * or game-over) into a screen-reader string. Lives per-app — mirrors
 * `BaseGameRecorder` and backgammon's announcer — because every game's
 * move shape differs and the shared abstraction has too little value until
 * a third copy lands.
 */
export class MoveAnnouncer {
  private readonly t: Translator;
  private readonly provider: AnnouncerContextProvider;
  // Track whose turn was last announced so we don't re-fire after every
  // internal state copy / render tick.
  private lastAnnouncedTurn: Color | null = null;

  constructor(t: Translator, provider: AnnouncerContextProvider) {
    this.t = t;
    this.provider = provider;
  }

  /** Forget the last-announced turn — call on a fresh game / rematch. */
  resetTurnMemory(): void {
    this.lastAnnouncedTurn = null;
  }

  /** Narrate a committed move. Returns null if there's nothing to say. */
  describeMove(move: Move, mover: Color, moverKindBefore: PieceKind): NarratedEvent {
    const subject = this.subjectFor(mover);
    const pieceWord = this.t(
      moverKindBefore === 'king' ? 'narration.pieces.king' : 'narration.pieces.man',
    );
    const from = squareToAlgebraic(move.from);
    const to = squareToAlgebraic(move.to);

    let text: string;
    if (move.captures.length === 0) {
      text = this.t('narration.move', { subject, piece: pieceWord, from, to });
    } else if (move.captures.length === 1) {
      // Russian + English both end the announcement the same way; the only
      // variant-sensitive bit is the chain-end framing for promotion below.
      text = this.t('narration.captureOne', { subject, piece: pieceWord, from, to });
    } else {
      text = this.t('narration.captureMany', {
        subject,
        piece: pieceWord,
        count: move.captures.length,
        from,
        to,
      });
    }

    if (move.promoted) {
      // English Draughts ends the chain on promotion; Russian Shashki
      // continues mid-chain. The variant difference shows up in the chain-end
      // copy: Russian gets "promoted to a king on h8."; English gets the
      // same square name with "promotion ended the chain." appended.
      const variant = this.provider.getVariant();
      const tail = this.t('narration.promotionTail', {
        subject,
        to,
      });
      text = `${text} ${tail}`;
      if (variant === 'english' && move.captures.length > 0) {
        text = `${text} ${this.t('narration.promotionEndsChain')}`;
      }
    }

    return { text };
  }

  /**
   * Narrate whose turn it is. Deduplicates against the last-announced colour
   * so repeated calls from the rAF loop don't spam the SR.
   */
  describeTurnStart(player: Color): NarratedEvent | null {
    if (this.lastAnnouncedTurn === player) return null;
    this.lastAnnouncedTurn = player;
    const localColor = this.provider.getLocalColor();
    if (localColor !== null && player === localColor) {
      return { text: this.t('narration.turnYou') };
    }
    const subject = this.subjectFor(player);
    return { text: this.t('narration.turn', { subject }) };
  }

  /**
   * Narrate the cursor's current square. `occupant` is the piece at that
   * square (or null for empty), `hint` flags whether the square is a valid
   * destination of an active selection. Always polite — cursor moves
   * shouldn't interrupt the SR.
   */
  describeCursor(
    square: Square,
    occupant: CursorOccupant | null,
    hint: CursorHint,
  ): NarratedEvent {
    const sqName = squareToAlgebraic(square);
    if (occupant === null) {
      if (hint === 'validCapture') {
        return { text: this.t('narration.cursor.validCapture', { square: sqName }) };
      }
      if (hint === 'validMove') {
        return { text: this.t('narration.cursor.validMove', { square: sqName }) };
      }
      return { text: this.t('narration.cursor.empty', { square: sqName }) };
    }
    const localColor = this.provider.getLocalColor();
    if (localColor === null) {
      // Hot-seat local: name pieces by colour, not "your" / "opponent's".
      const key = occupant.color === 'white'
        ? (occupant.kind === 'king' ? 'narration.cursor.whiteKing' : 'narration.cursor.whiteMan')
        : (occupant.kind === 'king' ? 'narration.cursor.blackKing' : 'narration.cursor.blackMan');
      return { text: this.t(key, { square: sqName }) };
    }
    const isOwn = occupant.color === localColor;
    const key = isOwn
      ? (occupant.kind === 'king' ? 'narration.cursor.ownKing' : 'narration.cursor.ownMan')
      : (occupant.kind === 'king' ? 'narration.cursor.oppKing' : 'narration.cursor.oppMan');
    return { text: this.t(key, { square: sqName }) };
  }

  /** Narrate piece selection (Enter on an own piece with legal moves). */
  describeSelection(square: Square, kind: PieceKind): NarratedEvent {
    const sqName = squareToAlgebraic(square);
    const key = kind === 'king' ? 'narration.selection.king' : 'narration.selection.man';
    return { text: this.t(key, { square: sqName }) };
  }

  /** Narrate clearing an active selection. */
  describeDeselect(): NarratedEvent {
    return { text: this.t('narration.selection.deselect') };
  }

  /**
   * Narrate one step of a multi-capture chain — the user pressed Enter on a
   * valid next landing square but the chain has further continuations, so
   * the move isn't committed yet. Pre-commit captures get a "chain
   * continues" tail so the user knows to keep stepping.
   */
  describeChainStep(captured: Square, landed: Square): NarratedEvent {
    return {
      text: this.t('narration.chainStep', {
        cap: squareToAlgebraic(captured),
        land: squareToAlgebraic(landed),
      }),
    };
  }

  /** Narrate the final result. Always assertive so the SR interrupts. */
  describeGameOver(outcome: GameOutcome): NarratedEvent {
    if (outcome.kind === 'draw') {
      return { text: this.t('narration.gameOverDraw'), assertive: true };
    }
    const localColor = this.provider.getLocalColor();
    if (localColor !== null && outcome.winner === localColor) {
      return { text: this.t('narration.gameOverYou'), assertive: true };
    }
    if (localColor !== null) {
      return { text: this.t('narration.gameOverLoss'), assertive: true };
    }
    const subject = this.subjectFor(outcome.winner);
    return { text: this.t('narration.gameOver', { subject }), assertive: true };
  }

  /**
   * Pick the subject phrase. In hot-seat local (no local seat) we name the
   * colour — both players share the keyboard, so "you" is ambiguous. In
   * vs-AI / online we use "you" for the local seat and the opponent name
   * (when known) for the remote seat.
   */
  private subjectFor(player: Color): string {
    if (this.provider.getMode() === 'local' || this.provider.getLocalColor() === null) {
      return player === 'white'
        ? this.t('narration.subjects.white')
        : this.t('narration.subjects.black');
    }
    if (player === this.provider.getLocalColor()) {
      return this.provider.getLocalName() ?? this.t('narration.subjects.you');
    }
    return this.provider.getOpponentName() ?? this.t('narration.subjects.opponent');
  }
}
